import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ChatState from "../../../../components/chat/chatState/ChatState";
import AlertCircleIcon from "../../../../components/icons/AlertCircleIcon";
import CloseIcon from "../../../../components/icons/CloseIcon";
import SearchIcon from "../../../../components/icons/SearchIcon";
import SupportTicketsSidebarIcon from "../../../../components/icons/SupportTicketsSidebarIcon";
import Loader from "../../../../components/loader/Loader";
import Skeleton from "../../../../components/skeleton/Skeleton";
import {
  clearFilters,
  fetchTickets,
  filtersKey,
  openTicket,
  setFilter,
  setTicketListOpen,
} from "../../../../features/supportTickets/supportTicketsSlice";
import useInfiniteScroll from "../../../../hooks/useInfiniteScroll";
import { CATEGORY_OPTIONS, RATING_OPTIONS, STATUS_FILTERS, ticketCountLabel } from "../ticketMeta";
import OneTicket from "./oneTicket/OneTicket";
import styles from "./ticketList.module.css";

const SEARCH_DELAY_MS = 350;
const SKELETON_ROWS = 7;

const ListSkeleton = () => (
  <div className={styles.skeletonList} aria-hidden="true">
    {Array.from({ length: SKELETON_ROWS }, (_, index) => (
      <div key={index} className={styles.skeletonRow}>
        <Skeleton width={`${52 + ((index * 17) % 34)}%`} height={13} />
        <Skeleton width={`${40 + ((index * 23) % 30)}%`} height={10} />
        <Skeleton width={`${66 + ((index * 13) % 28)}%`} height={10} />
      </div>
    ))}
  </div>
);

// The ticket queue: search (subject, code or player), status chips,
// category and rating, then the tickets, newest activity first.
const TicketList = () => {
  const dispatch = useDispatch();
  const tickets = useSelector((state) => state.supportTickets.tickets);
  const total = useSelector((state) => state.supportTickets.total);
  const status = useSelector((state) => state.supportTickets.listStatus);
  const requestPage = useSelector((state) => state.supportTickets.listRequestPage);
  const listKey = useSelector((state) => state.supportTickets.listKey);
  const filters = useSelector((state) => state.supportTickets.filters);
  const { page, hasMore } = useSelector((state) => state.supportTickets.pagination);
  const activeTicketId = useSelector((state) => state.supportTickets.activeTicketId);
  const timeZone = useSelector((state) => state.timeZone.timeZone);

  const [query, setQuery] = useState(filters.search);
  const key = filtersKey(filters);

  // While open, a live update for a ticket the list doesn't hold fetches it.
  useEffect(() => {
    dispatch(setTicketListOpen(true));
    return () => dispatch(setTicketListOpen(false));
  }, [dispatch]);

  // Typing settles into the search filter.
  useEffect(() => {
    const value = query.trim();
    if (value === filters.search) return undefined;
    const timer = setTimeout(() => dispatch(setFilter({ key: "search", value })), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [dispatch, query, filters.search]);

  // Opening the page and every filter change load the first page.
  useEffect(() => {
    dispatch(fetchTickets({ page: 1 }));
  }, [dispatch, key]);

  const changeFilter = (filter, value) => dispatch(setFilter({ key: filter, value }));

  const clearSearch = () => {
    setQuery("");
    changeFilter("search", "");
  };

  const resetFilters = () => {
    setQuery("");
    dispatch(clearFilters());
  };

  const handleOpen = useCallback((ticket) => dispatch(openTicket(ticket)), [dispatch]);

  const loadMore = useCallback(() => {
    dispatch(fetchTickets({ page: page + 1 }));
  }, [dispatch, page]);

  const reload = () => dispatch(fetchTickets({ page: 1 }));

  const scrollRef = useRef(null);
  useInfiniteScroll({
    ref: scrollRef,
    // A failed page waits for its retry button instead of re-firing on scroll.
    hasMore: hasMore && status !== "failed",
    isLoading: status === "loading",
    onLoadMore: loadMore,
  });

  const loading = status === "loading";
  const failed = status === "failed";
  const firstPagePending = loading && requestPage === 1;
  const showSkeleton = firstPagePending && tickets.length === 0;
  // Rows from the previous filters stay, faded, until the new ones land.
  const stale = firstPagePending && listKey !== key;
  const empty = status === "succeeded" && tickets.length === 0;
  const filtered = Boolean(filters.status || filters.category || filters.rating || filters.search);

  return (
    <div className={styles.ticketList}>
      <div className={styles.filters}>
        <label className={styles.searchField}>
          <SearchIcon className={styles.searchIcon} />
          <input
            type="text"
            dir="auto"
            className={styles.searchInput}
            placeholder="ابحث بالعنوان أو الرمز أو اسم اللاعب"
            aria-label="البحث في التذاكر"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") changeFilter("search", query.trim());
              if (event.key === "Escape" && query) clearSearch();
            }}
          />
          {firstPagePending && filters.search && <Loader size={16} />}
          {query && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={clearSearch}
              aria-label="مسح البحث"
            >
              <CloseIcon />
            </button>
          )}
        </label>

        <div className={styles.chips} role="group" aria-label="الحالة">
          {STATUS_FILTERS.map((option) => (
            <button
              key={option.value || "all"}
              type="button"
              aria-pressed={filters.status === option.value}
              className={`${styles.chip} ${filters.status === option.value ? styles.chipActive : ""}`}
              onClick={() => changeFilter("status", option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className={styles.selects}>
          <select
            className={styles.select}
            value={filters.category}
            onChange={(event) => changeFilter("category", event.target.value)}
            aria-label="التصنيف"
          >
            {CATEGORY_OPTIONS.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            className={styles.select}
            value={filters.rating}
            onChange={(event) => changeFilter("rating", event.target.value)}
            aria-label="التقييم"
          >
            {RATING_OPTIONS.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.summary}>
          <span aria-live="polite">
            {status === "succeeded" && tickets.length > 0 ? ticketCountLabel(total) : ""}
          </span>
          {filtered && (
            <button type="button" className={styles.linkButton} onClick={resetFilters}>
              مسح الفلاتر
            </button>
          )}
        </div>
      </div>

      <div
        className={`${styles.progress} ${firstPagePending && tickets.length > 0 ? styles.progressActive : ""}`}
        aria-hidden="true"
      />

      <div
        className={`${styles.scroller} ${stale ? styles.stale : ""}`}
        ref={scrollRef}
        aria-busy={loading}
      >
        {showSkeleton && <ListSkeleton />}

        {failed && requestPage === 1 && tickets.length === 0 && (
          <ChatState
            tone="error"
            icon={<AlertCircleIcon />}
            title="تعذّر تحميل التذاكر"
            text="تحقق من الاتصال ثم حاول مرة أخرى."
            actionLabel="إعادة المحاولة"
            onAction={reload}
          />
        )}

        {failed && requestPage === 1 && tickets.length > 0 && (
          <div className={styles.banner} role="alert">
            <span>تعذّر تحديث القائمة</span>
            <button type="button" className={styles.linkButton} onClick={reload}>
              إعادة المحاولة
            </button>
          </div>
        )}

        {empty &&
          (filtered ? (
            <ChatState
              icon={<SearchIcon />}
              title="لا توجد تذاكر مطابقة"
              text={filters.search ? `لا شيء يطابق «${filters.search}» مع الفلاتر الحالية.` : "لا توجد تذاكر بهذه الفلاتر."}
              actionLabel="مسح الفلاتر"
              onAction={resetFilters}
            />
          ) : (
            <ChatState
              icon={<SupportTicketsSidebarIcon />}
              title="لا توجد تذاكر بعد"
              text="ستظهر هنا تذاكر اللاعبين فور فتحها."
            />
          ))}

        {tickets.map((ticket) => (
          <OneTicket
            key={ticket._id}
            ticket={ticket}
            isActive={ticket._id === activeTicketId}
            onOpen={handleOpen}
            timeZone={timeZone}
          />
        ))}

        {loading && requestPage > 1 && (
          <div className={styles.footerRow}>
            <Loader size={18} />
            <span>جارٍ تحميل المزيد…</span>
          </div>
        )}

        {failed && requestPage > 1 && (
          <div className={styles.footerRow} role="alert">
            <span>تعذّر تحميل المزيد</span>
            <button type="button" className={styles.linkButton} onClick={loadMore}>
              إعادة المحاولة
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketList;
